import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { LibraryMusicUnfilled } from '@dsny/dsny-component-library/icons'
import {
  Cover,
  CoverImage,
  EmptyCover,
  IconCircle,
  ImageFallback
} from './PlaylistCoverMosaic.styles'

const getImageUrl = (candidate) => {
  return typeof candidate === 'string' ? candidate.trim() : ''
}

export const getPlaylistCoverImages = (playlist = {}) => {
  const candidates = Array.isArray(playlist.thumbnails)
    ? playlist.thumbnails
    : [playlist.thumbnail]

  return [...new Set(candidates.map(getImageUrl).filter(Boolean))].slice(0, 4)
}

const Placeholder = ({ compact = false }) => (
  <ImageFallback aria-hidden="true">
    <IconCircle $compact={compact}>
      <LibraryMusicUnfilled color="#717070" />
    </IconCircle>
  </ImageFallback>
)

Placeholder.propTypes = {
  compact: PropTypes.bool
}

const PlaylistCoverMosaic = ({ ariaLabel, playlist }) => {
  const images = useMemo(() => getPlaylistCoverImages(playlist), [playlist])
  const imageKey = images.join('|')
  const [failedImages, setFailedImages] = useState(() => new Set())

  useEffect(() => {
    setFailedImages(new Set())
  }, [imageKey])

  const accessibleLabel = ariaLabel || playlist?.name
  const isLabelled = Boolean(accessibleLabel)

  if (images.length === 0) {
    return (
      <EmptyCover
        aria-hidden={isLabelled ? undefined : true}
        aria-label={isLabelled ? accessibleLabel : undefined}
        data-cover-count="0"
        data-mosaic-layout="empty"
        role={isLabelled ? 'img' : undefined}>
        <IconCircle>
          <LibraryMusicUnfilled color="#717070" />
        </IconCircle>
      </EmptyCover>
    )
  }

  const layoutNames = ['empty', 'single', 'stacked', 'three', 'grid']

  return (
    <Cover
      $count={images.length}
      aria-hidden={isLabelled ? undefined : true}
      aria-label={isLabelled ? accessibleLabel : undefined}
      data-cover-count={images.length}
      data-mosaic-layout={layoutNames[images.length]}
      role={isLabelled ? 'img' : undefined}>
      {images.map((src, index) =>
        failedImages.has(src) ? (
          <Placeholder key={src} compact={images.length > 1} />
        ) : (
          <CoverImage
            key={src}
            alt=""
            aria-hidden="true"
            data-mosaic-slot={index + 1}
            src={src}
            onError={() => {
              setFailedImages((current) => new Set(current).add(src))
            }} />
        )
      )}
    </Cover>
  )
}

PlaylistCoverMosaic.propTypes = {
  ariaLabel: PropTypes.string,
  playlist: PropTypes.object.isRequired
}

export default PlaylistCoverMosaic
